import React, { useState } from "react";
import { onlineTotals } from "../../utils/onlineTotals";
import FlashIcon from "../../assets/svg/FlashIcon";
import DeviderBorder from "../utils/DeviderBorder";
import PayOnline from "./PayOnline";
import PayCod from "./PayCod";
import PayPartial from "./PayPartial";
import PayUpi from "./PayUpi";
import PayCard from "./PayCard";
import PayNetBanking from "./PayNetBanking";
import PayWallet from "./PayWallet";
import useCart from "../../hooks/useCart";
import useCoupon from "../../hooks/useCoupon";
import useWallet from "../../hooks/useWallet";
import PlaceOrderFree from "./PlaceOrderFree";
import PlaceOrderWallet from "./PlaceOrderWallet";
import WalletCouponSheet from "../utils/WalletCouponSheet";

const PaymentMethods = ({ container }) => {
  const { cartData } = useCart();
  const { couponData, setCouponData } = useCoupon();
  const { balance, useWalletCredit, setUseWalletCredit } = useWallet();
  const [askWallet, setAskWallet] = useState(false);

  // HAYD RULE: a coupon and the HAYD wallet cannot be used together.
  // Turning the wallet on removes any coupon (after asking); turning it off brings back the best coupon.
  const turnWalletOn = () => {
    setCouponData((prev) => ({ ...prev, applied: false, appliedCoupon: null, suppressed: true }));
    setUseWalletCredit(true);
    setAskWallet(false);
  };
  const onWalletToggle = (checked) => {
    if (!checked) {
      setUseWalletCredit(false);
      setCouponData((prev) => ({
        ...prev,
        suppressed: false,
        applied: Boolean(prev?.bestCoupon),
        appliedCoupon: prev?.bestCoupon || null,
      }));
      return;
    }
    if (couponData?.applied && couponData?.appliedCoupon) setAskWallet(true);
    else turnWalletOn();
  };

  // const subTotal = cartData?.items?.reduce((acc, item) => {
  //   const price = item.compare_at_price || item.price || 0; // fallback to current price
  //   return acc + price * item.quantity;
  // }, 0);

  // // CALCULATE REGULAR PRICE DYNAMICALLY
  // const regularTotal = cartData?.items?.reduce((acc, item) => {
  //   const price = item.price || 0; // fallback to current price
  //   return acc + price * item.quantity;
  // }, 0);

  const subTotal = cartData?.items?.reduce((acc, item) => {
    const isUpsell = item.properties?.upsell_discount === "true";

    if (isUpsell) {
      // upsell item → use regular price for subtotal
      return acc + (item.price || 0) * item.quantity;
    }

    const price = item.compare_at_price || item.price || 0;
    return acc + price * item.quantity;
  }, 0);

  const regularTotal = cartData?.items?.reduce((acc, item) => {
    const isUpsell = item.properties?.upsell_discount === "true";
    const upsellPrice = item.properties?.upsell_price;

    if (isUpsell && upsellPrice) {
      // upsell item → use upsell_price (in rupees, convert to paise)
      return acc + upsellPrice * item.quantity * 100;
    }

    return acc + (item.price || 0) * item.quantity;
  }, 0);

  const formattedSubTotal = (subTotal / 100).toFixed(0);
  const formattedRegularTotal = (regularTotal / 100).toFixed(2);

  // Keep a payable balance for small orders instead of hiding online methods.
  const { payable: showOnline, prepaidDiscount } = onlineTotals(formattedRegularTotal, couponData?.applied ? couponData?.appliedCoupon?.discountAmount : 0);

  const codTotal =
    (couponData?.appliedCoupon?.discountAmount
      ? Number(formattedRegularTotal) - couponData.appliedCoupon.discountAmount
      : Number(formattedRegularTotal)) + 150;
  const walletForOnline = useWalletCredit ? Math.min(Number(balance), Number(showOnline) > 0 ? Number(showOnline) : 0) : 0;
  const walletForCod = useWalletCredit ? Math.min(Number(balance), codTotal) : 0;

  // The wallet covers the whole prepaid amount: no card step needed.
  const walletCoversAll =
    useWalletCredit && Number(showOnline) > 0 && Number(balance) >= Number(showOnline);

  const showPlaceOrder = couponData?.appliedCoupon?.discountAmount
    ? formattedRegularTotal - couponData?.appliedCoupon?.discountAmount == 0
      ? true
      : false
    : false;

  return (
    <div className="payment-method-inner bg-white rounded-lg p-4">
      <div>
        <p className="text-[18px] supreme-bold">Pay via</p>
        <div className="flex items-center gap-1 mt-2">
          <FlashIcon width={14} height={14} className="text-blue-500" />
          <p className="text-[12px] general-sans-medium">
            Enjoy fast delivery on all prepaid orders.
          </p>
        </div>
      </div>
      <div className="mt-4"></div>
      {balance > 0 && (
        <div className="mb-4 border border-gray-200 rounded-lg p-3">
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <p className="text-[15px] supreme-medium">Use HAYD wallet</p>
              <p className="text-[12px] general-sans-regular text-gray-500">
                Balance ₹{Number(balance).toFixed(0)}
              </p>
            </div>
            <input
              type="checkbox"
              checked={useWalletCredit}
              onChange={(e) => onWalletToggle(e.target.checked)}
              className="w-5 h-5 accent-black cursor-pointer"
            />
          </label>
          {useWalletCredit && (
            <p className="mt-2 text-[12px] general-sans-medium text-[#238017]">
              ₹{Math.min(Number(balance), Number(showOnline) > 0 ? Number(showOnline) : 0).toFixed(0)} from your wallet will be applied at payment.
            </p>
          )}
          {useWalletCredit && (
            <p className="mt-1 text-[12px] general-sans-regular text-gray-500">
              Cash on delivery isn't available when you use your HAYD wallet.
            </p>
          )}
        </div>
      )}
      {/* <PayUpi />
      <div>
        <DeviderBorder />
      </div> */}

      {walletCoversAll ? (
        <>
          <PlaceOrderWallet payable={showOnline} />
          <div>
            <DeviderBorder />
          </div>
        </>
      ) : showOnline > 0 ? (
        <>
          <PayOnline
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
            walletApplied={walletForOnline}
            onlinePayable={showOnline}
            prepaidDiscount={prepaidDiscount}
          />
          <div>
            <DeviderBorder />
          </div>
          <PayCard
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
            walletApplied={walletForOnline}
            onlinePayable={showOnline}
            prepaidDiscount={prepaidDiscount}
          />
          <div>
            <DeviderBorder />
          </div>
          <PayNetBanking
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
            walletApplied={walletForOnline}
            onlinePayable={showOnline}
            prepaidDiscount={prepaidDiscount}
          />
          <div>
            <DeviderBorder />
          </div>
          <PayWallet
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
            walletApplied={walletForOnline}
            onlinePayable={showOnline}
            prepaidDiscount={prepaidDiscount}
          />
          <div>
            <DeviderBorder />
          </div>
          {/* <PayPartial />
              <div>
                <DeviderBorder />
              </div> */}
        </>
      ) : null}

      {showPlaceOrder ? (
        <>
          <PlaceOrderFree
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
          />
          <div>
            <DeviderBorder />
          </div>
        </>
      ) : null}

      {/* HAYD RULE: the wallet is used with online payment only. A cash on delivery order has no payment in Shopify,
          so a cancelled order could not return the wallet money as store credit. */}
      {!useWalletCredit && (
        <>
          <PayCod
            regularPrice={formattedRegularTotal}
            comparePrice={formattedSubTotal}
            container={container}
            walletApplied={walletForCod}
          />
          <div>
            <DeviderBorder />
          </div>
        </>
      )}
      <WalletCouponSheet
        open={askWallet}
        container={container}
        title={`${couponData?.appliedCoupon?.coupon_name || "This coupon"} cannot be used with your HAYD wallet`}
        message={`Use your ₹${Number(balance).toFixed(0)} wallet balance instead? The coupon will be removed from this order.`}
        confirmLabel="Use wallet"
        cancelLabel="Keep coupon"
        onConfirm={turnWalletOn}
        onCancel={() => setAskWallet(false)}
      />
    </div>
  );
};

export default PaymentMethods;
